import { useMemo } from 'react';
import {
  BarChartOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FireOutlined,
  InboxOutlined,
  RiseOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserAddOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useGetDashboardQuery } from '@/modules/dashboard/dashboardApi';
import { BreakdownBarChart, BreakdownPieChart, TrendLineChart } from '@/pages/reports/ReportCharts';
import { formatInr, formatReportPercent } from '@/utils/currency';
import { buildHeatmapFromOrders, mergeHeatmapCells } from '@/utils/dashboardHeatmap';
import { formatDateTime } from '@/utils/date';
import { ucfirst } from '@/utils/string';
import { DashboardHeader } from './components/DashboardHeader';
import { DashboardSnapshotStrip } from './components/DashboardSnapshotStrip';
import { DashboardStatCard } from './components/DashboardStatCard';
import { OrderHeatmap } from './components/OrderHeatmap';
import { OrdersMap } from './components/orders-map/OrdersMap';
import { useDashboardAccess } from './hooks/useDashboardAccess';
import { useDashboardQueryParams } from './hooks/useDashboardQueryParams';
import { growthHint } from './utils/growthHint';
import styles from './Dashboard.module.scss';

export default function Dashboard() {
  const { state, apiParams, dateRangeValue, patchParams, resetFilters } = useDashboardQueryParams();
  const access = useDashboardAccess();

  const { data, isFetching } = useGetDashboardQuery(apiParams, {
    refetchOnMountOrArgChange: true,
  });

  useGlobalLoader(isFetching);

  const summary = data?.summary;

  const heatmapData = useMemo(() => {
    if (!data) return [];
    return mergeHeatmapCells(
      data.orderHeatmap ?? [],
      buildHeatmapFromOrders(data.recentOrders ?? []),
    );
  }, [data]);

  const showOperationsSection =
    access.canViewOrders ||
    access.canViewProducts ||
    access.canViewCustomers ||
    access.canViewInventory;

  return (
    <div className={styles.page}>
      <DashboardHeader
        dateRangeValue={dateRangeValue}
        groupBy={state.groupBy}
        roleLabel={access.roleLabel}
        canViewOrders={access.canViewOrders}
        canViewReports={access.canViewReports}
        onDateRangeChange={(dateFrom, dateTo) => patchParams({ dateFrom, dateTo })}
        onGroupByChange={(groupBy) => patchParams({ groupBy })}
        onReset={resetFilters}
      />

      {summary ? (
        <>
          {access.canViewSales ? (
            <DashboardSnapshotStrip
              summary={summary}
              showRevenue={access.canViewOrders || access.canViewReports}
              showOrders={access.canViewOrders}
              showCustomers={access.canViewCustomers}
              showAov={access.canViewOrders}
            />
          ) : null}

          {access.canViewSales ? (
            <div className={styles.kpiGrid}>
              {access.canViewOrders || access.canViewReports ? (
                <DashboardStatCard
                  featured
                  tone="gold"
                  icon={<DollarOutlined />}
                  label="Revenue"
                  value={formatInr(summary.revenue)}
                  hint={growthHint(summary.revenueGrowth)}
                />
              ) : null}
              {access.canViewOrders ? (
                <DashboardStatCard
                  featured
                  tone="green"
                  icon={<ShoppingCartOutlined />}
                  label="Orders"
                  value={String(summary.orders)}
                  hint={growthHint(summary.ordersGrowth)}
                />
              ) : null}
              {access.canViewOrders ? (
                <DashboardStatCard
                  featured
                  tone="amber"
                  icon={<RiseOutlined />}
                  label="Avg order value"
                  value={formatInr(summary.averageOrderValue)}
                  hint={`${summary.itemsSold} items sold`}
                />
              ) : null}
              {access.canViewOrders ? (
                <DashboardStatCard
                  featured
                  tone="slate"
                  icon={<DollarOutlined />}
                  label="Today"
                  value={formatInr(summary.todayRevenue)}
                  hint={`${summary.todayOrders} orders today`}
                />
              ) : null}
            </div>
          ) : null}

          {access.canViewCustomers || access.canViewOrders || access.canViewInventory ? (
            <div className={styles.kpiGridSecondary}>
              {access.canViewCustomers ? (
                <DashboardStatCard
                  tone="gold"
                  icon={<TeamOutlined />}
                  label="Customers"
                  value={String(summary.totalCustomers)}
                  hint={`${summary.newCustomers} new signups`}
                />
              ) : null}
              {access.canViewCustomers && access.canViewOrders ? (
                <DashboardStatCard
                  tone="amber"
                  icon={<UserAddOutlined />}
                  label="Active buyers"
                  value={String(summary.activeCustomers)}
                  hint={`${formatReportPercent(summary.conversionRate)} paid`}
                />
              ) : null}
              {access.canViewOrders ? (
                <DashboardStatCard
                  tone="green"
                  icon={<InboxOutlined />}
                  label="Pending fulfilment"
                  value={String(summary.pendingOrders)}
                  hint={`${summary.paidOrders} paid orders`}
                />
              ) : null}
              {access.canViewInventory ? (
                <DashboardStatCard
                  tone="rose"
                  icon={<WarningOutlined />}
                  label="Stock alerts"
                  value={String(summary.lowStockItems + summary.outOfStockItems)}
                  hint={`${summary.outOfStockItems} out of stock`}
                />
              ) : null}
            </div>
          ) : null}

          {access.canViewOrders ? (
            <section className={styles.insightsSection}>
              <div className={styles.sectionHeading}>
                <EnvironmentOutlined />
                <div>
                  <h2 className={styles.sectionTitle}>Geography & activity</h2>
                  <p className={styles.sectionSubtitle}>Where orders come from and when they peak</p>
                </div>
              </div>
              <div className={styles.bentoRow}>
                <OrdersMap points={data.ordersMap ?? []} salesByCity={data.salesByCity ?? []} />
                <OrderHeatmap data={heatmapData} totalOrders={summary.orders} />
              </div>
            </section>
          ) : null}

          {access.canViewSales ? (
            <section className={styles.insightsSection}>
              <div className={styles.sectionHeading}>
                <BarChartOutlined />
                <div>
                  <h2 className={styles.sectionTitle}>Sales trends</h2>
                  <p className={styles.sectionSubtitle}>Revenue, orders, and payment breakdown</p>
                </div>
              </div>
              <div className={styles.bentoRowWide}>
                <TrendLineChart
                  title="Revenue trend"
                  data={data.revenueTrend ?? []}
                  valueLabel="Revenue"
                  currency
                />
                <TrendLineChart
                  title="Orders trend"
                  data={data.ordersTrend ?? []}
                  valueLabel="Orders"
                />
              </div>

              <div className={styles.bentoRowWide}>
                <BreakdownPieChart title="Orders by status" data={data.orderStatusBreakdown ?? []} />
                <BreakdownPieChart title="Payment status" data={data.paymentStatusBreakdown ?? []} />
              </div>

              <BreakdownBarChart
                title="Revenue by payment method"
                data={data.paymentMethodBreakdown ?? []}
              />
            </section>
          ) : null}

          {showOperationsSection ? (
            <section className={styles.insightsSection}>
              <div className={styles.sectionHeading}>
                <ShoppingCartOutlined />
                <div>
                  <h2 className={styles.sectionTitle}>Operations</h2>
                  <p className={styles.sectionSubtitle}>
                    {access.canViewInventory && !access.canViewOrders
                      ? 'Catalog performance and stock health'
                      : 'Recent orders, top sellers, and customers'}
                  </p>
                </div>
              </div>
              <div className={styles.bentoTriple}>
                {access.canViewOrders ? (
                  <div className={styles.dataPanel}>
                    <div className={styles.panelHeader}>
                      <h3 className={styles.panelTitle}>Recent orders</h3>
                      <Link to={ROUTES.ORDERS} className={styles.panelLink}>
                        View all
                      </Link>
                    </div>
                    <div className={styles.listBody}>
                      {(data.recentOrders ?? []).length > 0 ? (
                        data.recentOrders.map((order) => (
                          <div key={order.id} className={styles.listRow}>
                            <div className={styles.listMain}>
                              <Link to={`${ROUTES.ORDERS}/${order.id}`} className={styles.orderLink}>
                                {order.orderNumber}
                              </Link>
                              <span className={styles.listSub}>
                                {order.customerName} · {formatDateTime(order.placedAt)}
                              </span>
                            </div>
                            <span className={styles.listValue}>{formatInr(order.grandTotal)}</span>
                            <span className={styles.listTag}>{ucfirst(order.orderStatus)}</span>
                          </div>
                        ))
                      ) : (
                        <div className={styles.emptyWrap}>No orders in this period.</div>
                      )}
                    </div>
                  </div>
                ) : null}

                {access.canViewProducts ? (
                  <div className={styles.dataPanel}>
                    <div className={styles.panelHeader}>
                      <h3 className={styles.panelTitle}>Top products</h3>
                      {access.canViewReports ? (
                        <Link to={`${ROUTES.REPORTS}?tab=sales`} className={styles.panelLink}>
                          Report
                        </Link>
                      ) : null}
                    </div>
                    <div className={styles.listBody}>
                      {(data.topProducts ?? []).length > 0 ? (
                        data.topProducts.map((product, index) => (
                          <div key={product.sku} className={styles.listRow}>
                            <div className={styles.listRowInner}>
                              <span className={styles.listRank}>{index + 1}</span>
                              <div className={styles.listMain}>
                                <Link
                                  to={`${ROUTES.PRODUCTS}/${product.productId}/view`}
                                  className={styles.orderLink}
                                >
                                  {product.productName}
                                </Link>
                                <span className={styles.listSub}>
                                  {product.quantitySold} units sold
                                </span>
                              </div>
                            </div>
                            <span className={styles.listValue}>{formatInr(product.revenue)}</span>
                          </div>
                        ))
                      ) : (
                        <div className={styles.emptyWrap}>No product sales yet.</div>
                      )}
                    </div>
                  </div>
                ) : null}

                {access.canViewCustomers ? (
                  <div className={styles.dataPanel}>
                    <div className={styles.panelHeader}>
                      <h3 className={styles.panelTitle}>Top customers</h3>
                      {access.canViewReports ? (
                        <Link to={`${ROUTES.REPORTS}?tab=customers`} className={styles.panelLink}>
                          Report
                        </Link>
                      ) : null}
                    </div>
                    <div className={styles.listBody}>
                      {(data.topCustomers ?? []).length > 0 ? (
                        data.topCustomers.map((customer, index) => (
                          <div key={customer.customerId} className={styles.listRow}>
                            <div className={styles.listRowInner}>
                              <span className={styles.listRank}>{index + 1}</span>
                              <div className={styles.listMain}>
                                <Link
                                  to={`${ROUTES.CUSTOMERS}/${customer.customerId}`}
                                  className={styles.orderLink}
                                >
                                  {customer.customerName}
                                </Link>
                                <span className={styles.listSub}>
                                  <EnvironmentOutlined /> {customer.city || '—'} ·{' '}
                                  {customer.totalOrders} orders
                                </span>
                              </div>
                            </div>
                            <span className={styles.listValue}>{formatInr(customer.totalSpent)}</span>
                          </div>
                        ))
                      ) : (
                        <div className={styles.emptyWrap}>No customer sales yet.</div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              {access.canViewInventory ? (
                (data.lowStockItems ?? []).length > 0 ? (
                  <div className={`${styles.dataPanel} ${styles.alertPanel}`}>
                    <div className={styles.panelHeader}>
                      <h3 className={styles.panelTitle}>
                        <WarningOutlined /> Inventory alerts
                      </h3>
                      <Link to={ROUTES.INVENTORY} className={styles.panelLink}>
                        Manage
                      </Link>
                    </div>
                    <div className={styles.listBody}>
                      {data.lowStockItems.map((item) => (
                        <div key={`${item.sku}-${item.warehouseName}`} className={styles.listRow}>
                          <div className={styles.listMain}>
                            <Link
                              to={`${ROUTES.PRODUCTS}/${item.productId}/view`}
                              className={styles.orderLink}
                            >
                              {item.productName}
                            </Link>
                            <span className={styles.listSub}>
                              {item.sku} · {item.warehouseName} · {item.availableQty} available
                            </span>
                          </div>
                          <span className={styles.listValue}>{formatInr(item.stockValue)}</span>
                          <span
                            className={`${styles.listTag} ${
                              item.stockStatus === 'out_of_stock'
                                ? styles.status_out
                                : styles.status_low
                            }`}
                          >
                            {item.stockStatus === 'out_of_stock' ? 'Out of stock' : 'Low stock'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.dataPanel}>
                    <div className={styles.panelHeader}>
                      <h3 className={styles.panelTitle}>Inventory</h3>
                      <Link to={ROUTES.INVENTORY} className={styles.panelLink}>
                        Manage
                      </Link>
                    </div>
                    <div className={styles.emptyWrap}>
                      <FireOutlined /> All stock healthy — {formatInr(summary.stockValue)} value across{' '}
                      {summary.totalProducts} products.
                    </div>
                  </div>
                )
              ) : null}
            </section>
          ) : null}

          {!access.hasVisibleWidgets ? (
            <div className={styles.dataPanel}>
              <div className={styles.emptyWrap}>
                No dashboard widgets are enabled for your role. Contact a super admin to update
                your permissions.
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
